// The visual journey through the site: what each finished project can show next — its best photo,
// how close its 3D was to the real home, and its before/after — plus the "next project" order.
// Prompts across the site are built from this, so they always point at real, existing visuals.

import { PROJECT_PHOTOS, type ProjectPhoto } from './projectPhotos';
import { PROJECT_STORIES } from './projectStories';
import imageSizes from './imageSizes.json';

const SIZES = imageSizes as unknown as Record<string, [number, number]>;

export interface Visual {
  src: string;
  alt: string;
  width: number;
  height: number;
}

function sized(p: ProjectPhoto | undefined): Visual | undefined {
  const s = p && SIZES[p.src];
  return p && s ? { src: p.src, alt: p.alt, width: s[0], height: s[1] } : undefined;
}

export interface ProjectTeaser {
  slug: string;
  name: string;
  tagline: string;
  summary: string;
  where: string; // e.g. "Independent villa · Anjanapura, JP Nagar, Bangalore"
  href: string;
  photo: Visual;
  /** First 3D render + the finished photo of the same space */
  compare?: { render: Visual; actual: Visual; label: string };
  /** First before + after pair (only some projects have these) */
  beforeAfter?: { before: Visual; after: Visual; label: string };
}

// Order shown on the Projects page — "Next project" follows it and wraps around
export const PROJECT_ORDER = Object.keys(PROJECT_STORIES);

export function projectTeaser(slug: string): ProjectTeaser {
  const story = PROJECT_STORIES[slug];
  const set = PROJECT_PHOTOS[slug];
  if (!story || !set) throw new Error(`Unknown project: ${slug}`);
  const c = set.comparisons?.[0];
  const b = set.beforeAfter?.[0];
  const render = sized(c?.render);
  const actual = sized(c?.actual);
  const before = sized(b?.before);
  const after = sized(b?.after);
  return {
    slug,
    name: story.name,
    tagline: story.tagline,
    summary: story.cardDescription,
    where: `${story.propertyType} · ${story.location}`,
    href: `/projects/${slug}`,
    photo: (sized(set.hero) ?? sized(set.gallery[0]))!,
    compare: render && actual ? { render, actual, label: c!.label } : undefined,
    beforeAfter: before && after ? { before, after, label: b!.label } : undefined,
  };
}

export function nextProject(slug: string): ProjectTeaser {
  const i = PROJECT_ORDER.indexOf(slug);
  return projectTeaser(PROJECT_ORDER[(i + 1) % PROJECT_ORDER.length]);
}

// Deep links that open a project page straight on a comparison tab
export const compareHref = (slug: string) => `/projects/${slug}#3d-vs-actual`;
export const beforeAfterHref = (slug: string) => `/projects/${slug}#before-after`;
